import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { Loader } from '@rikstv/shared-components';

import { ErrorPage } from '../components/errorPage/ErrorPage';
import { isLoadingUserInfo, isUserInfoUnavailable } from '../forces/loadUserInfo/selectors';
import { useIsAuthenticated } from '../hooks/useIsAuthenticated';
import { authService } from '../utils/auth/AuthService';

import style from './WaitForStartupWrapper.module.css';

interface Props {
  waitForStartup: boolean;
  loginRequired: boolean; // show page only if user is logged in
  internalOnly?: boolean; // silently redirect everyone but logged in internal users to redirectPath
  children: React.ReactElement;
  redirectPath: string;
}

export const WaitForStartupWrapper: React.FC<Props> = ({
  children,
  loginRequired,
  internalOnly = false,
  redirectPath,
  ...access
}) => {
  const isLoggedIn = useIsAuthenticated();

  const isUserInfoLoading = useSelector(isLoadingUserInfo);
  const hasNoUserInfo = useSelector(isUserInfoUnavailable);
  const navigate = useNavigate();

  // Hidden pages: no login prompt, just pretend the page does not exist for anyone
  // who is not a logged in internal user. Checked before the waitForStartup
  // early-return so it also protects routes that skip startup.
  if (internalOnly && (!isLoggedIn || !authService.isInternalUser())) {
    return <Navigate to={redirectPath} replace />;
  }

  // no waiting for these ones
  if (!access.waitForStartup) {
    return children;
  }

  // A failed refresh keeps the page on the user info loaded earlier in the session
  if (isLoggedIn && hasNoUserInfo) {
    return <ErrorPage />;
  }

  if (isLoggedIn && isUserInfoLoading) {
    return (
      <div className={style.wrapper}>
        <Loader defaultValue={500} size="large" label="Henter innhold" />
      </div>
    );
  }

  // Page is protected and requires authenticated user
  if (loginRequired && !isLoggedIn) {
    // Go to frontpage if reauthentication fails
    authService.login().catch(() => navigate(redirectPath));
    return null;
  }

  return children;
};
